import React, { createContext, useContext, ReactNode, useEffect } from 'react';
import { useUserQuery } from '@/hooks/useUserQuery';
import { UserResponse } from '@/api/user';
import store from '@/store';
import tracker from '@/tracker';
import * as Sentry from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import { APIError, callPostApi } from '@/components/api-common';
import { HTTPError, TimeoutError } from 'ky';

export type AuthState = {
  isAuthenticated: boolean | null;
  isLoading: boolean;
  error: Error | null;
  isNetworkError: boolean;
  isServerError: boolean;
  user: UserResponse | null;
};

type AuthContextType = AuthState & {
  refetch: () => Promise<any>;
  authenticate: () => Promise<void>;
  logout: (redirectUrl?: string, callback?: () => void) => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const DISABLE_INTERCOM_PATTERNS = [
  /^\/app\/subscribe$/,
  /^\/proposal_media_list$/,
  /^\/product_media_preview\/[^/]+(?:\/[^/]+)?$/,
];

function shouldAutoBootIntercom(): boolean {
  const pathname = window.location.pathname;

  if (DISABLE_INTERCOM_PATTERNS.some(pattern => pattern.test(pathname))) {
    return false;
  }

  const searchParams = new URLSearchParams(window.location.search);
  if (searchParams.get('iframe_in_enterprise') === '1') {
    return false;
  }

  return true;
}

function adaptUserResponse(currentUser: UserResponse) {
  // Adapt the UserResponse to the shape expected by the Redux store
  return {
    ...currentUser,
    org_type: currentUser.default_org_type,
    isAdmin: currentUser.is_admin,
    id: currentUser.user_id,
  };
}

function onUserAuthenticated(currentUser: UserResponse) {
  if (
    process.env.ENABLE_PROFITWELL == 'true' &&
    currentUser.stripe_customer_id
  ) {
    window.profitwell?.('start', { user_id: currentUser.stripe_customer_id });
  }

  // First dispatch to Redux store to ensure user data is available for future tracking
  store.dispatch({
    type: 'LOGIN',
    user: adaptUserResponse(currentUser),
  });

  // Pass only minimal identity information needed for initialization
  tracker.onLogin(
    {
      email: currentUser.email,
      userId: currentUser.user_id,
      username: currentUser.username,
    },
    {
      intercom: {
        autoBoot: shouldAutoBootIntercom(),
      },
    }
  );

  // Set user context for Sentry
  Sentry.setUser({
    email: currentUser.email,
    id: currentUser.user_id,
    username: currentUser.username,
  });
}

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  // Use React Query for all user data and auth state
  // Always enable the query to check auth status on app startup
  const { data: user, isLoading, error, refetch } = useUserQuery();
  const queryClient = useQueryClient();

  function isServerError() {
    if (!error) {
      return false;
    }
    if (error instanceof HTTPError) {
      return error.response.status >= 500 && error.response.status < 600;
    }
    return false;
  }
  function isNetworkError() {
    if (!error) {
      return false;
    }
    if (error instanceof TimeoutError) {
      return true;
    }

    // network error is just a TypeError
    return error instanceof TypeError;
  }
  function isAuthenticationError(): boolean | null {
    if (error) {
      if (error instanceof APIError && error.code == 115) {
        return false;
      }
    }
    if (user) {
      return true;
    }
    return null;
  }

  // Transform React Query state into auth state
  const authState: AuthState = {
    user: user || null,
    isLoading,
    error: error as Error | null,
    isServerError: isServerError(),
    isNetworkError: isNetworkError(),
    isAuthenticated: isAuthenticationError(),
  };

  const authenticate = async (): Promise<void> => {
    // Force a fresh fetch after login
    const res = await refetch();
    if (res.data) {
      onUserAuthenticated(res.data);
    }
  };

  const logout = async (
    redirectUrl?: string,
    callback?: () => void
  ): Promise<void> => {
    // Log the logout event
    tracker.logEvent('web_logout');

    const clearAuthState = () => {
      // Clear Redux state
      store.dispatch({ type: 'LOGOUT' });

      // Clear Sentry user context
      Sentry.setUser(null);

      // Clear local storage
      localStorage.removeItem('archiving-tasks');
      localStorage.removeItem('project-preview');

      // Clear tracking state
      tracker.onLogout();

      // Invalidate the user query - this will force a refetch on next login
      // which will trigger the useEffect and call onUserAuthenticated again
      queryClient.invalidateQueries({ queryKey: ['user'] });
    };

    const handlePostLogout = () => {
      if (callback) {
        callback();
      } else {
        // Navigation needs to be handled by the calling component
        // since we can't use useNavigate hook inside this context
        window.location.href = redirectUrl || '/login';
      }
    };

    try {
      // Call the logout API
      await callPostApi('user/service/userAuth/logout/v2/');
    } catch (error) {
      // Log error but continue with logout
      console.error('Logout API failed:', error);
    }

    // Always clear state and navigate, regardless of API result
    clearAuthState();
    handlePostLogout();
  };

  // Automatically sync auth state when user data becomes available
  useEffect(() => {
    if (user) {
      onUserAuthenticated(user);
    }
  }, [user]);

  const refetchUser = async () => {
    const res = await refetch();
    if (res.data) {
      store.dispatch({ type: 'REFRESH', user: adaptUserResponse(res.data) });
    }
  };

  const contextValue: AuthContextType = {
    ...authState,
    refetch: refetchUser,
    authenticate,
    logout,
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
